/**
 * Éditeur complet d'une vidéo YouTube Studio — partagé par les routes
 * « nouvelle vidéo » et « édition ». Compose les 6 sections (attempt, fond,
 * musique, overlay, paramètres, publication) + actions enregistrer / lancer.
 *
 * On sélectionne un ATTEMPT (réponse enregistrée d'un élève), pas un exercice :
 * le showcase rejoue un attempt → on garantit toujours un replay filmable.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@jeraisonne/ui';
import { Save, Play, Loader2 } from 'lucide-react';
import {
  VideoSpecSchema,
  DEFAULT_OVERLAY,
  type VideoOverlay,
  type DurationSeconds,
  type AspectRatio,
  type SocialPrivacy,
} from '@jeraisonne/db/schemas/youtube';
import {
  buildYoutubeDescription,
  buildYoutubeTags,
  buildYoutubeTitle,
  estimateShowcaseDurationMs,
  formatDurationMs,
  captureWindowMs,
} from '@jeraisonne/db/youtube';
import { SUBJECTS } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useUpsertYoutubeVideo, useEnqueueRender } from '@/lib/admin/youtube-mutations';
import {
  useExercisesQuestionCount,
  useBackgroundSignedUrl,
  useYoutubeVideoItems,
  type YoutubeVideoRow,
} from '@/lib/admin/youtube-queries';
import { AttemptPicker, type AttemptSelection } from './attempt-picker';
import { BackgroundPicker, MusicPicker } from './asset-pickers';
import { OverlayEditor, OverlayPreview } from './overlay-editor';
import { DurationSettings } from './duration-settings';
import { PublishPanel, type PublishConfig } from './publish-panel';
import { sectionCls } from './ui';

type InitialVideo = YoutubeVideoRow & { exercise?: { id: string; title: string } | null };

interface VideoEditorProps {
  initial?: InitialVideo | null;
}

export function VideoEditor({ initial }: VideoEditorProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const upsert = useUpsertYoutubeVideo();
  const enqueue = useEnqueueRender();

  // Playlist ordonnée. value[0] = item primaire (métadonnées + miniature).
  const [attempts, setAttempts] = useState<AttemptSelection[]>(
    initial?.attempt_id
      ? [
          {
            attemptId: initial.attempt_id,
            exerciseId: initial.exercise_id,
            title: initial.exercise?.title ?? '',
            niveau: '',
            chapitre: '',
            matiere: '',
          },
        ]
      : [],
  );
  const attempt = attempts[0] ?? null;

  // À l'édition : hydrate la playlist complète depuis youtube_video_items (le
  // state initial ci-dessus ne connaît que l'item primaire).
  const { data: items } = useYoutubeVideoItems(initial?.id ?? '', !!initial?.id);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || !items || items.length === 0) return;
    setAttempts(
      items.map((it) => ({
        attemptId: it.attempt_id ?? '',
        exerciseId: it.exercise_id,
        title: it.title,
        niveau: it.level ? String(it.level) : '',
        chapitre: it.chapter_title ?? '',
        matiere: SUBJECTS.find((s) => s.value === it.subject)?.label ?? '',
      })),
    );
    setHydrated(true);
  }, [items, hydrated]);
  const [backgroundAssetId, setBackgroundAssetId] = useState<string | null>(
    initial?.background_asset_id ?? null,
  );
  const [musicAssetId, setMusicAssetId] = useState<string | null>(initial?.music_asset_id ?? null);
  const [duration, setDuration] = useState<DurationSeconds>(
    (initial?.duration_seconds as DurationSeconds) ?? 60,
  );
  const [aspect, setAspect] = useState<AspectRatio>(initial?.aspect_ratio ?? 'landscape');
  const [loopBackground, setLoopBackground] = useState(initial?.loop_background ?? true);
  const [overlay, setOverlay] = useState<VideoOverlay>(initial?.overlay ?? DEFAULT_OVERLAY);
  const { data: background } = useBackgroundSignedUrl(backgroundAssetId);
  const [publish, setPublish] = useState<PublishConfig>({
    privacy: 'private',
    scheduledAt: '',
    ytTitle: initial?.yt_title ?? '',
    ytDescription: initial?.yt_description ?? '',
    ytTags: initial?.yt_tags ?? [],
  });

  // Pré-remplit overlay + métadonnées YouTube à la sélection d'un attempt.
  useEffect(() => {
    if (!attempt) return;
    setOverlay((prev) => ({
      ...prev,
      title: prev.title || attempt.title,
      level: prev.level || attempt.niveau,
      subject: prev.subject || attempt.matiere,
    }));
    setPublish((prev) => ({
      ...prev,
      ytTitle: prev.ytTitle || buildYoutubeTitle({ titre: attempt.title, niveau: attempt.niveau }),
      ytDescription:
        prev.ytDescription ||
        buildYoutubeDescription({
          niveau: attempt.niveau,
          matiere: attempt.matiere,
          exercises: attempts.map((a) => ({ titre: a.title, exerciseId: a.exerciseId })),
        }),
      ytTags: prev.ytTags.length
        ? prev.ytTags
        : buildYoutubeTags({
            niveau: attempt.niveau,
            matiere: attempt.matiere,
            chapitre: attempt.chapitre,
          }),
    }));
  }, [attempt, attempts]);

  const canSave = attempts.length > 0 && !!user;

  function buildSpec() {
    if (attempts.length === 0) throw new Error('Aucun attempt sélectionné');
    const primary = attempts[0]!;
    return VideoSpecSchema.parse({
      exercise_id: primary.exerciseId,
      attempt_id: primary.attemptId || null,
      background_asset_id: backgroundAssetId,
      music_asset_id: musicAssetId,
      aspect_ratio: aspect,
      duration_seconds: duration,
      loop_background: loopBackground,
      overlay,
      yt_title: publish.ytTitle || null,
      yt_description: publish.ytDescription || null,
      yt_tags: publish.ytTags,
      items: attempts.map((a) => ({
        exercise_id: a.exerciseId,
        attempt_id: a.attemptId || null,
      })),
    });
  }

  async function handleSave(): Promise<string | null> {
    if (!canSave || !user) return null;
    const video = await upsert.mutateAsync({ id: initial?.id, userId: user.id, spec: buildSpec() });
    return video.id;
  }

  async function handleSaveOnly() {
    const id = await handleSave();
    if (id && !initial) navigate({ to: '/tutor/admin/youtube/$videoId', params: { videoId: id } });
  }

  async function handleLaunch() {
    const id = await handleSave();
    if (!id) return;
    await enqueue.mutateAsync({
      video_id: id,
      platforms: ['youtube'],
      privacy: publish.privacy as SocialPrivacy,
      scheduled_at: publish.scheduledAt ? new Date(publish.scheduledAt).toISOString() : null,
    });
    navigate({ to: '/tutor/admin/youtube/$videoId', params: { videoId: id } });
  }

  const busy = upsert.isPending || enqueue.isPending;
  const error = (upsert.error as Error | null)?.message ?? (enqueue.error as Error | null)?.message;

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
      <div className="space-y-5">
        <Section step={1} title="Réponses à filmer (playlist)">
          <AttemptPicker value={attempts} onChange={setAttempts} />
          {attempts.length > 0 && (
            <EstimatedDuration
              exerciseIds={attempts.map((a) => a.exerciseId)}
              targetSeconds={duration}
            />
          )}
        </Section>

        <Section step={2} title="Fond vidéo">
          <BackgroundPicker selectedId={backgroundAssetId} onSelect={setBackgroundAssetId} />
        </Section>

        <Section step={3} title="Musique">
          <MusicPicker selectedId={musicAssetId} onSelect={setMusicAssetId} />
        </Section>

        <Section step={4} title="Overlay">
          <OverlayEditor
            value={overlay}
            aspect={aspect}
            onChange={setOverlay}
            background={background}
            attemptId={attempt?.attemptId}
            exerciseId={attempt?.exerciseId}
          />
        </Section>

        <Section step={5} title="Paramètres vidéo">
          <DurationSettings
            duration={duration}
            aspect={aspect}
            loopBackground={loopBackground}
            onChange={(n) => {
              if (n.duration !== undefined) setDuration(n.duration);
              if (n.aspect !== undefined) setAspect(n.aspect);
              if (n.loopBackground !== undefined) setLoopBackground(n.loopBackground);
            }}
          />
        </Section>

        <Section step={6} title="Publication YouTube">
          <PublishPanel value={publish} onChange={setPublish} />
        </Section>

        {error && <p className="text-accent-600 font-sans text-sm">{error}</p>}

        {/* Actions — bouton primaire en premier dans le DOM (mobile-first) */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={handleLaunch}
            disabled={!canSave || busy}
            className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-12 items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] disabled:opacity-50 sm:order-2"
          >
            {enqueue.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Lancer le rendu
          </button>
          <button
            type="button"
            onClick={handleSaveOnly}
            disabled={!canSave || busy}
            className="border-ink-200 text-ink-700 font-display inline-flex h-12 items-center justify-center gap-2 rounded-xl border bg-white px-5 text-sm font-bold transition-all active:scale-[.98] disabled:opacity-50 sm:order-1"
          >
            {upsert.isPending && !enqueue.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Enregistrer le brouillon
          </button>
        </div>
      </div>

      {/* Aperçu complet collant : fond + téléphone + overlay (rendu final). */}
      <div className="lg:sticky lg:top-4 lg:self-start">
        <p className="font-display text-ink-400 mb-2 text-[11px] font-bold uppercase tracking-widest">
          Aperçu de la vidéo
        </p>
        {attempt ? (
          <OverlayPreview
            value={overlay}
            aspect={aspect}
            background={background}
            attemptId={attempt.attemptId}
            exerciseId={attempt.exerciseId}
          />
        ) : (
          <p className="border-ink-200 text-ink-400 rounded-xl border border-dashed p-6 text-center text-xs">
            Choisis une réponse (étape 1) pour l’aperçu.
          </p>
        )}
      </div>
    </div>
  );
}

function EstimatedDuration({
  exerciseIds,
  targetSeconds,
}: {
  exerciseIds: string[];
  targetSeconds: number;
}) {
  const { data: count, isLoading } = useExercisesQuestionCount(exerciseIds);
  if (isLoading) return null;
  const totalQuestions = count ?? 0;
  const baseMs = estimateShowcaseDurationMs(totalQuestions);
  const captureMs = captureWindowMs(targetSeconds);
  const stretched = captureMs > baseMs;
  return (
    <p className="text-ink-500 mt-2 font-sans text-xs">
      ⏱ {exerciseIds.length} exercice{exerciseIds.length > 1 ? 's' : ''} ·{' '}
      <strong>{totalQuestions}</strong> question{totalQuestions > 1 ? 's' : ''} (durée naturelle{' '}
      {formatDurationMs(baseMs)}) —{' '}
      {stretched ? (
        <>
          étiré sur <strong>{formatDurationMs(captureMs)}</strong>
          {captureMs < targetSeconds * 1000 ? ' puis bouclé' : ''} pour remplir la vidéo.
        </>
      ) : (
        <>joué à vitesse normale (durée cible courte).</>
      )}
    </p>
  );
}

function Section({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className={sectionCls}>
      <div className="mb-3 flex items-center gap-2">
        <span className="bg-primary-100 text-primary-700 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
          {step}
        </span>
        <h2 className="font-display text-ink-900 text-base font-extrabold">{title}</h2>
      </div>
      {children}
    </section>
  );
}
